import React, { memo, useContext } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useSelector } from 'react-redux'

import ConfigContext from '../../contexts/configContext'
import { translate } from '../../helpers/stringHelpers'
import { formatCurrency } from '../../helpers/currencyHelpers'

import Modal from '../modal'
import ModalBody from '../modalBody'
import Button from '../button'
import FormGroup from '../formGroup'
import FormInput from '../formInput'
import FeesAlert from '../feesAlert'

const PurchaseConfirmationModalBid = ({
  bidValue,
  bidCalculation,
  isAwaitingResponse,
  isOptedOut,
  listingUuid,
  onBidConfirmOptOut,
  onClose,
  onSubmit
}) => {
  const { locale } = useContext(ConfigContext)

  const listing = useSelector(state => state.lots.listings[listingUuid], shallowEqual)
  const isMarketplace = listing?.isMarketplace
  const currencyCode = useSelector(state => listingUuid && state.listingSales.sales[listingUuid].currencyCode)
  const marketplaceFriendlyAuctionUuid = isMarketplace ? 'marketplace' : listing?.auctionUuid
  const isPreBidding = useSelector(state => listing?.type === 'webcast' && state.auctions.information[marketplaceFriendlyAuctionUuid]?.preBidding)
  const showAutoBidOnly = useSelector(state => state.auctions.configurations[marketplaceFriendlyAuctionUuid]?.showAutoBidOnly)
  const hasBidButton = isPreBidding || (listing?.type === 'lot' && !showAutoBidOnly)
  if (!hasBidButton || !listing) return null
  const bidValueFormatted = formatCurrency(locale, currencyCode, bidValue)
  const bidString = bidCalculation
    ? translate('JspPublicCodes_JSP_LISTING_PRICE_PER_QUANTITY')
      .replace('{0}', bidValueFormatted)
    : bidValueFormatted
  const bidCalculationString = bidCalculation ? ` ${bidCalculation}` : ''
  return (
    <Modal onClose={onClose} isOpen={!!bidValue} id='bidModal'>
      <ModalBody>
        <h4 className='modal__primary-title' id='bid-confirm__title'>{translate('JspPublicCodes_JSP_BIDDING_PLACE_BID')}</h4>
        <h5 id='bid-confirm__title--listing'>{listing.title}</h5>
        <form
          className='clearfix'
          onSubmit={onSubmit}
        >
          <span
            id='bid-confirm__content'
            dangerouslySetInnerHTML={{
              __html: translate(isMarketplace ? 'JspPublicCodes_JSP_MARKETPLACE_BID_CONFIRMATION' : 'JspPublicCodes_JSP_LISTING_BID_CONFIRMATION')
                .replace('{0}', `<strong class='text-warning'>${bidString}<strong>${bidCalculationString}`)
                .replace('{1}', isMarketplace ? listing.id : listing.lotNumber)
            }}
          />
          <FormGroup>
            <FormInput
              id='bid-confirm__input--opt-out'
              type='checkbox'
              isChecked={isOptedOut}
              onChange={(isChecked) => onBidConfirmOptOut(listingUuid, isChecked)}
              className='text-muted'
            >
              {translate('JspPublicCodes_JSP_LISTING_BID_CONFIRMATION_OPT_OUT')}
            </FormInput>
          </FormGroup>
          <FeesAlert listingUuid={listingUuid} />
          <Button
            id='bid__cancel'
            onClick={onClose}
            className='bid__cta'
          >
            {translate('JSTextCodes_CANCEL')}
          </Button>
          <Button
            disabled={isAwaitingResponse}
            id='bid__submit'
            type='submit'
            color='primary'
            className='pull-right bid__cta'
          >
            {translate('JspPublicCodes_JSP_LISTING_BID_CONFIRMATION_CTA')}
          </Button>
        </form>
      </ModalBody>
    </Modal>
  )
}

PurchaseConfirmationModalBid.propTypes = {
  bidCalculation: PropTypes.string,
  bidValue: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  isAwaitingResponse: PropTypes.bool,
  isOptedOut: PropTypes.bool,
  listingUuid: PropTypes.string,
  onBidConfirmOptOut: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
}

const isEqual = (a, b) => a.bidValue === b.bidValue &&
  a.bidCalculation === b.bidCalculation &&
  a.isOptedOut === b.isOptedOut &&
  a.isAwaitingResponse === b.isAwaitingResponse &&
  a.listingUuid === b.listingUuid

export default memo(PurchaseConfirmationModalBid, isEqual)
